import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { Spinner } from "reactstrap";
import { object, string } from "yup";

import Toast from "shared/toast";
import InlineEditor from "shared/react_components/InlineEditor";

import StaticName from "./StaticName";

import { GetIntegrationName, UpdateIntegration } from "integrations/pages/Integrations.graphql";
import { types, metadata } from "integrations/integrationsMetadata";

const schema = object().shape({
  name: string().required("Name can't be blank")
});

const IntegrationName = ({ id, type }) => {
  const { data: queryData, error, loading } = useQuery(GetIntegrationName, {
    variables: { id },
    skip: id === undefined
  });

  const [updateIntegration, { data: mutationData }] = useMutation(UpdateIntegration);

  const editableName = () => {
    if (mutationData && mutationData.updateIntegration.integration) {
      return mutationData.updateIntegration.integration.name;
    } else {
      return queryData.integration.name;
    }
  };

  const onSubmit = useCallback(
    async ({ name }, { setFieldError }) => {
      try {
        const {
          data: {
            updateIntegration: { integration, errors }
          }
        } = await updateIntegration({
          variables: { id: id, attributes: { name } }
        });

        if (integration) {
          Toast.success({
            message: `Successfully renamed Connect App to "${integration.name}".`
          });

          return true;
        } else {
          errors.forEach(({ attribute, message }) =>
            setFieldError(
              attribute,
              `${attribute.charAt(0).toUpperCase()}${attribute.slice(
                1
              )} ${message}`
            )
          );
        }
      } catch (error) {
        Toast.error({
          title: "Couldn't Save Connect App Name",
          message:
            "An error occurred while saving the new Connect App name.  Please try again."
        });
      }

      return false;
    },
    [updateIntegration, id]
  );

  const integrationName = () => {
    if (error || id === undefined) {
      return <StaticName name={metadata[type].name} error={!!error}/>;
    } else if (loading) {
      return <Spinner />;
    } else {
      return <InlineEditor
        label="Connect App Name"
        name="name"
        initialValues={{ name:  editableName() }}
        validationSchema={schema}
        onSubmit={onSubmit}
      />;
    }
  };

  return (
    <>
      { integrationName() }
    </>
  );
};

IntegrationName.propTypes = {
  id: PropTypes.number,
  type: PropTypes.oneOf(types).isRequired
};

export default IntegrationName;
